import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { saveAs } from '@progress/kendo-file-saver';
import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectorRef, Component, OnInit, OnDestroy, AfterViewChecked, ViewChild } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { PatientImportEdit } from '../../model/patient-import';
import { PatientImportListService } from 'src/app/core/service/grid-data/grid-data-patient-import.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { PatientImportService } from 'src/app/core/service/patient-import.service';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';

@Component({
  standalone: false,
  selector: 'app-patient-import-list',
  templateUrl: './patient-import-list.component.html',
  styleUrls: ['./patient-import-list.component.scss']
})
export class PatientImportListComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public exportDialogOpened = false;
  public deleteDialogOpened = false;
  public importDialogOpened = false;
  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public editDataItem: PatientImportEdit;
  public selectedPatientImport: PatientImportEdit;
  public communityEnabled = false;
  public patientLabel = {};
  public importStages = [];
  public showPatientsTab = false;
  public selectedRecord: number[] = [];
  public exportForm: any;
  public selectedTab = 0;
  public currentState: State = null;

  /**
   * Patient Import list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'lastModifiedOn', dir: 'desc' }
    ]
  };

  public filterText = '';
  public isNew = false;

  private listSubscription: Subscription;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private searchHelperService: SearchHelperService,
    private patientImportListService: PatientImportListService,
    private fb: UntypedFormBuilder,
    private appConfigService: AppConfigService,
    private settingService: SettingService,
    private messageHandlerService: MessageHandlerService,
    private patientImportService: PatientImportService,
    private translate: TranslateService,
    private toastrService: ToastrService,
    private router: Router
  )
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.patientImportListService ? this.patientImportListService.loading : false;
  }

  public get exportType(): UntypedFormControl
  {
    return this.exportForm.get('exportType');
  }

  public onTabSelect(e: SelectEvent): void
  {
    const tabChanged = e.index !== this.selectedTab;
    this.selectedTab = e.index;
    // Automatically refresh
    if (tabChanged) 
    {
      if (this.selectedTab === 0) 
      {
        this.getPatientImportBatches(this.currentState !== null ? this.currentState : null);
      }
      else if (this.selectedTab === 1)
      {
        this.currentState = this.state;
      }
    }
  }

  public ngOnInit(): void
  {
    this.importStages = this.patientImportService.patientImportStages;
    if (!this.communityEnabled)
    {
      this.importStages = this.importStages.filter(x => x.value !== 'READY_TO_SEND_INVITE' && x.value !== 'SENDING_INVITE');
    }

    this.listSubscription = this.patientImportListService.subscribe({
      next: (value) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            if (d.lastModifiedOn !== null)
            {
              d.lastModifiedOn = new Date(d.lastModifiedOn);
            }
            d.documentName = d.importDocument !== null ? d.importDocument.name : '';
            d.channelName = d.channel !== null ? d.channel.name : '';
            d.stageName = this.patientImportService.getPatientImportStageText(d.importStage);
          });
        }

        // TODO - this interaction between the component and the data service can be simplified / clean up.
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.count = value.total : this.count = 0
        };
      }
    });
    this.getPatientImportBatches();
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  public ngAfterViewChecked(): void
  {
    this.changeDetectorRef.detectChanges();
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.executeQuery();
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.executeQuery();
  }

  /**
   * Add patient import batch handler
   */
  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new PatientImportEdit();
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Save patient import batch handler.
   *
   * @param patientImport Patient import batch object to save.
   */
  public saveChangesHandler(patientImport: PatientImportEdit): void
  {
    this.patientImportService.savePatientImportBatch(patientImport, this.isNew, patientImport.id).subscribe({
      next: () =>
      {
        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('PATIENT-IMPORT-BATCH-SAVED-SUCCESSFULLY');
        this.getPatientImportBatches();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Cancel patient import batch changes.
   */
  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Edit handler
   *
   * @param id Patient import batch id.
   */
  public editHandler(id: number): void
  {
    this.isNew = false;
    this.patientImportService.getPatientImportBatchById(id).subscribe({
      next: (data) =>
      {
        if (data.locked && data.importStage !== 'COMPLETED')
        {
          this.messageHandlerService.displayWarning('BATCH-LOCKED');
          return;
        }
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    this.patientImportService.getPatientImportBatchById(id).subscribe({
      next: (data) =>
      {
        // If locked we don't allow deletion except if it is completed stage
        if (data.locked && data.importStage !== 'COMPLETED')
        {
          this.messageHandlerService.displayWarning('PATIENT-IMPORT-LOCKED-CANNOT-DELETE');
        }
        else
        {
          this.selectedPatientImport = data as PatientImportEdit;
          this.open('deleteDialog');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const patientImportBatchId = this.selectedPatientImport.id;
      this.patientImportService.deletePatientImportBatch(patientImportBatchId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('PATIENT-IMPORT-BATCH-DELETED-SUCCESSFULLY');
          this.selectedPatientImport = null;
          this.getPatientImportBatches();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    this.close('deleteDialog');
  }

  public importAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the import
      const patientImportBatchId = this.selectedPatientImport.id;
      this.patientImportService.processPatientImportBatch(patientImportBatchId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('BATCH-PROCESSED-SUCCESSFULLY');
          this.selectedPatientImport = null;
          this.getPatientImportBatches();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    this.close('importDialog');
  }

  /**
   * Handles the row selection change of the Kendo grid.
   *
   * @param selections Kendo grid selected keys.
   */
  public onSelectedKeysChange(selections: Array<number>): void
  {
    if (selections.length > 0)
    {
      const id = selections[0]; // we only allow single selection
      const selectedItem = this.selectedPatientImport = this.gridView.data.find(x => x.id === id);
      this.selectedPatientImport = selectedItem !== undefined ? selectedItem : null;
    }
    else // if the row is unselected.
    {
      this.selectedPatientImport = null;
    }
    this.showPatientsTab = this.selectedPatientImport !== null;
  }

  /**
   * Perform grid refresh.
   */
  public refresh(): void
  {
    this.getPatientImportBatches();
  }

  /**
   * Process the patient import batch.
   *
   * @param id Patient import batch id.
   */
  public importHandler(id: number): void
  {
    this.patientImportService.getPatientImportBatchById(id).subscribe({
      next: (data: any) =>
      {
        // If locked we don't allow deletion except if it is completed stage
        if (data.locked)
        {
          const messageKey = data.importStage === 'COMPLETED' ? 'BATCH-COMPLETED' : 'BATCH-LOCKED';
          this.messageHandlerService.displayWarning(messageKey);
        }
        else if (data.organization === null)
        {
          this.messageHandlerService.displayWarning('PATIENT-IMPORT-MISSING-ORGANIZATION');
        }
        else
        {
          this.settingService.getSettingByKey('SubjectIdentifiers', data.organization.id).subscribe({
            next: (setting: any) =>
            {
              if (setting.value === '')
              {
                this.translate.get('WARNING-SUBJECT-IDENTIFIERS-NOT-DEFINED').subscribe({
                  next: (res) =>
                  {
                    this.toastrService.warning(res, null,
                      {
                        enableHtml: true,
                        timeOut: 10000     // Use a longer timeout than usual
                      })
                      .onTap.subscribe({
                        next: () =>
                        {
                          this.router.navigate(['/system-settings']);
                        }
                      });
                  }
                });
              }
              else
              {
                this.selectedPatientImport = data as PatientImportEdit;
                this.open('importDialog');
              }
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Select patients tab on batch grid double click
   *
   * @param event MouseEvent
   *
   */
  public dblClickImportBatchEvent(event: any): void
  {
    const rowIndex = event.target.parentElement.rowIndex;
    if (typeof rowIndex === 'number' && rowIndex < this.gridView.data.length)
    {
      if (this.showPatientsTab)
      {
        this.selectedTab = 1;
        this.tabstrip.selectTab(this.selectedTab);
      }
    }
  }

  /**
   * Download the import items for a batch
   *
   * @param id Import batch id
   */
  public downloadHandler(id: number): void
  {
    this.patientImportService.getPatientImportBatchById(id).subscribe({
      next: (data) =>
      {
        // If locked we don't allow deletion except if it is completed stage
        if (data.importStage === 'VALIDATING')
        {
          this.messageHandlerService.displayWarning('DOWNLOAD-PROCESS-BATCH-IMPORTING');
        }
        else
        {
          this.exportForm = this.fb.group({ exportType: [null, Validators.required] });
          this.selectedPatientImport = data as PatientImportEdit;
          this.open('exportDialog');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public exportAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const patientImportBatchId = this.selectedPatientImport.id;
      this.patientImportService.getPatientExportBatchItemsToCSV(patientImportBatchId, this.exportType.value).subscribe({
        next: (data: any) =>
        {
          this.selectedPatientImport = null;
          if (data.count === 0)
          {
            this.messageHandlerService.displaySuccess('DOWNLOAD-PROCESS-BATCH-NO-DATA');
          }
          else
          {
            saveAs(`data:${data.document.mimeType};base64,${data.document.file}`, data.document.name);
          }
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    this.close('exportDialog');
  }

  /**
   * Get patient import batches from API per the applied filter.
   *
   * @param filter Task filter string.
   */
  private getPatientImportBatches(currentState: State = null): void
  {
    if (currentState !== null)
    {
      this.state = currentState;
    }
    else
    {
      this.state = {
        filter: {
          filters: [
            { field: 'deleted', operator: 'eq', value: false }
          ], logic: 'and'
         }, group: [], skip: 0,
        sort: [
          { field: 'lastModifiedOn', dir: 'desc' }
        ],
        take: this.appConfigService.getConfig().pageSize
      };
   } 
   this.executeQuery();
  }

  /**
   * Execute patient import list query
   */
  private executeQuery(): void
  {
    this.selectedPatientImport = null;
    this.selectedRecord = [];
    this.showPatientsTab = false;
    this.patientImportListService.queryByState(this.state);
  }
}
